import {
  Directive,
  ElementRef,
  HostListener,
  inject,
  input,
  model,
  OnDestroy,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core';
import {
  ConnectedPosition,
  Overlay,
  OverlayPositionBuilder,
  OverlayRef,
} from '@angular/cdk/overlay';
import { ComponentPortal, TemplatePortal } from '@angular/cdk/portal';
import { TooltipComponent } from './tooltip.component';

export type TooltipPosition = 'top' | 'bottom' | 'left' | 'right';

@Directive({
  selector: '[sharedCoreTooltip]',
  standalone: true,
})
export class TooltipDirective implements OnDestroy {
  readonly tooltip = model<string | TemplateRef<any>>('', { alias: 'sharedCoreTooltip' });
  readonly position = input<TooltipPosition>('top');

  readonly dataTestId = input<string>('');

  private readonly overlay = inject(Overlay);
  private readonly positionBuilder = inject(OverlayPositionBuilder);
  private readonly elementRef = inject(ElementRef);
  private readonly viewContainerRef = inject(ViewContainerRef);

  private overlayRef: OverlayRef | null = null;

  private readonly positions: Record<TooltipPosition, ConnectedPosition> = {
    left: {
      originX: 'start',
      originY: 'center',
      overlayX: 'end',
      overlayY: 'center',
      offsetX: -10,
    },
    right: {
      originX: 'end',
      originY: 'center',
      overlayX: 'start',
      overlayY: 'center',
      offsetX: 10,
    },
    top: {
      originX: 'center',
      originY: 'top',
      overlayX: 'center',
      overlayY: 'bottom',
      offsetY: -10,
    },
    bottom: {
      originX: 'center',
      originY: 'bottom',
      overlayX: 'center',
      overlayY: 'top',
      offsetY: 10,
    },
  };

  @HostListener('mouseenter')
  show(): void {
    if (!this.tooltip()) return;

    const positionStrategy = this.positionBuilder
      .flexibleConnectedTo(this.elementRef)
      .withPositions([this.positions[this.position()]]);

    this.overlayRef = this.overlay.create({ positionStrategy });

    if (this.tooltip() instanceof TemplateRef) {
      const tooltipPortal = new TemplatePortal(
        this.tooltip() as TemplateRef<any>,
        this.viewContainerRef,
      );
      this.overlayRef.attach(tooltipPortal);
    } else {
      const tooltipPortal = new ComponentPortal(TooltipComponent);
      const tooltipRef = this.overlayRef.attach(tooltipPortal);
      tooltipRef.setInput('tooltip', this.tooltip());
      tooltipRef.setInput('dataTestId', this.dataTestId());
    }
  }

  @HostListener('mouseleave')
  hide(): void {
    this.overlayRef?.dispose();
    this.overlayRef = null;
  }

  ngOnDestroy(): void {
    this.overlayRef?.dispose();
  }
}
